import { useEffect, useMemo, useRef, useState } from "react";
import gsap from "gsap";

const STORAGE_KEY = "savoinimaux_cookie_preferences";

const defaultPreferences = {
  necessary: true,
  analytics: false,
  externalMedia: false,
};

export default function CookieConsent() {
  const bannerRef = useRef(null);
  const panelRef = useRef(null);

  const [visible, setVisible] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [preferences, setPreferences] = useState(defaultPreferences);

  const hasSavedPreferences = useMemo(() => {
    return !!localStorage.getItem(STORAGE_KEY);
  }, []);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);

      if (!saved) {
        setVisible(true);
        return;
      }

      const parsed = JSON.parse(saved);
      setPreferences({
        necessary: true,
        analytics: !!parsed.analytics,
        externalMedia: !!parsed.externalMedia,
      });
    } catch {
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    if (!visible || !bannerRef.current) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        bannerRef.current,
        {
          opacity: 0,
          y: 30,
          scale: 0.98,
          filter: "blur(8px)",
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          filter: "blur(0px)",
          duration: 0.7,
          ease: "power3.out",
        }
      );
    });

    return () => ctx.revert();
  }, [visible]);

  useEffect(() => {
    if (!panelRef.current) return;

    if (showPanel) {
      gsap.fromTo(
        panelRef.current,
        {
          opacity: 0,
          y: 18,
          scale: 0.985,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.45,
          ease: "power3.out",
        }
      );
    }
  }, [showPanel]);

  const savePreferences = (prefs) => {
    const finalPrefs = {
      necessary: true,
      analytics: !!prefs.analytics,
      externalMedia: !!prefs.externalMedia,
      updatedAt: new Date().toISOString(),
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(finalPrefs));
    window.dispatchEvent(new CustomEvent("cookiePreferencesUpdated", { detail: finalPrefs }));
    setPreferences(finalPrefs);
    setVisible(false);
    setShowPanel(false);
  };

  const acceptAll = () => {
    savePreferences({
      necessary: true,
      analytics: true,
      externalMedia: true,
    });
  };

  const refuseOptional = () => {
    savePreferences({
      necessary: true,
      analytics: false,
      externalMedia: false,
    });
  };

  const togglePreference = (key) => {
    if (key === "necessary") return;

    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const saveCustom = () => {
    savePreferences(preferences);
  };

  const reopenManager = () => {
    setVisible(true);
    setShowPanel(true);
  };

  useEffect(() => {
    window.openCookiePreferences = reopenManager;
    return () => {
      delete window.openCookiePreferences;
    };
  }, []);

  if (!visible) return null;

  return (
    <>
      <div
        ref={bannerRef}
        style={{
          position: "fixed",
          left: "50%",
          bottom: 20,
          transform: "translateX(-50%)",
          width: "min(94vw, 760px)",
          zIndex: 9999,
          borderRadius: "26px",
          border: "1px solid rgba(255,255,255,0.72)",
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.82), rgba(255,255,255,0.62))",
          backdropFilter: "blur(22px)",
          WebkitBackdropFilter: "blur(22px)",
          boxShadow:
            "0 24px 80px rgba(0,0,0,0.16), 0 8px 30px rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background:
              "radial-gradient(circle at top left, rgba(213,72,58,0.10), transparent 30%), radial-gradient(circle at right, rgba(240,138,100,0.10), transparent 28%)",
          }}
        />

        <div
          style={{
            position: "relative",
            padding: "22px 22px 18px",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 12px",
              borderRadius: 999,
              background: "rgba(255,255,255,0.7)",
              border: "1px solid rgba(255,255,255,0.85)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
              marginBottom: 14,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 999,
                background: "#d5483a",
              }}
            />
            <span
              style={{
                fontSize: 11,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "#b3261e",
                fontWeight: 700,
              }}
            >
              Préférences cookies
            </span>
          </div>

          <h3
            style={{
              margin: "0 0 10px",
              fontSize: "clamp(20px, 2vw, 26px)",
              lineHeight: 1.15,
              color: "#151515",
              fontWeight: 700,
              letterSpacing: "-0.02em",
            }}
          >
            Nous respectons votre vie privée.
          </h3>

          <p
            style={{
              margin: 0,
              fontSize: 14.5,
              lineHeight: 1.8,
              color: "rgba(21,21,21,0.72)",
              maxWidth: 620,
            }}
          >
            Nous utilisons des cookies nécessaires au fonctionnement du site, et,
            avec votre accord, des cookies de mesure d’audience et de contenus
            externes pour améliorer votre expérience.
          </p>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
              marginTop: 18,
            }}
          >
            <button onClick={refuseOptional} style={secondaryBtn}>
              Refuser
            </button>

            <button onClick={() => setShowPanel((prev) => !prev)} style={secondaryBtn}>
              {showPanel ? "Masquer les options" : "Personnaliser"}
            </button>

            <button onClick={acceptAll} style={primaryBtn}>
              Tout accepter
            </button>
          </div>

          {showPanel && (
            <div
              ref={panelRef}
              style={{
                marginTop: 18,
                padding: 14,
                borderRadius: 20,
                background: "rgba(255,255,255,0.64)",
                border: "1px solid rgba(255,255,255,0.78)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.4)",
              }}
            >
              <CookieRow
                title="Cookies nécessaires"
                description="Indispensables au fonctionnement du site, à la sécurité et à la mémorisation de votre choix."
                checked={true}
                disabled={true}
                onChange={() => {}}
              />

              <CookieRow
                title="Mesure d’audience"
                description="Permet d’analyser la fréquentation du site et d’améliorer les contenus."
                checked={preferences.analytics}
                onChange={() => togglePreference("analytics")}
              />

              <CookieRow
                title="Contenus externes"
                description="Permet d’afficher certains contenus intégrés comme cartes, vidéos ou widgets externes."
                checked={preferences.externalMedia}
                onChange={() => togglePreference("externalMedia")}
              />

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 14,
                }}
              >
                <button onClick={saveCustom} style={primaryBtn}>
                  Enregistrer mes choix
                </button>
              </div>
            </div>
          )}

          <p
            style={{
              margin: "14px 0 0",
              fontSize: 12.5,
              lineHeight: 1.7,
              color: "rgba(21,21,21,0.5)",
            }}
          >
            Vous pourrez modifier vos préférences à tout moment depuis le lien
            “Gérer mes cookies” dans le footer.
          </p>
        </div>
      </div>

      {!hasSavedPreferences && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(20,20,20,0.08)",
            backdropFilter: "blur(2px)",
            WebkitBackdropFilter: "blur(2px)",
            zIndex: 9998,
            pointerEvents: "none",
          }}
        />
      )}
    </>
  );
}

function CookieRow({ title, description, checked, onChange, disabled = false }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 14,
        alignItems: "center",
        padding: "14px 8px",
        borderBottom: "1px solid rgba(21,21,21,0.06)",
      }}
    >
      <div>
        <div
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: "#151515",
            marginBottom: 4,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 13.5,
            lineHeight: 1.7,
            color: "rgba(21,21,21,0.62)",
            maxWidth: 520,
          }}
        >
          {description}
        </div>
      </div>

      <button
        type="button"
        onClick={onChange}
        disabled={disabled}
        aria-pressed={checked}
        style={{
          position: "relative",
          width: 54,
          height: 32,
          borderRadius: 999,
          border: "none",
          cursor: disabled ? "not-allowed" : "pointer",
          background: checked
            ? "linear-gradient(90deg,#b3261e 0%,#d5483a 100%)"
            : "rgba(0,0,0,0.12)",
          transition: "all 0.25s ease",
          opacity: disabled ? 0.9 : 1,
          boxShadow: checked
            ? "0 8px 20px rgba(179,38,30,0.22)"
            : "inset 0 1px 2px rgba(0,0,0,0.08)",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 3,
            left: checked ? 25 : 3,
            width: 26,
            height: 26,
            borderRadius: 999,
            background: "#fff",
            transition: "all 0.25s ease",
            boxShadow: "0 4px 12px rgba(0,0,0,0.14)",
          }}
        />
      </button>
    </div>
  );
}

const primaryBtn = {
  appearance: "none",
  border: "none",
  cursor: "pointer",
  padding: "12px 18px",
  borderRadius: 999,
  color: "#fff",
  fontWeight: 700,
  fontSize: 14,
  background: "linear-gradient(90deg,#b3261e 0%,#d5483a 100%)",
  boxShadow: "0 14px 35px rgba(179,38,30,0.22)",
};

const secondaryBtn = {
  appearance: "none",
  border: "1px solid rgba(21,21,21,0.08)",
  cursor: "pointer",
  padding: "12px 18px",
  borderRadius: 999,
  color: "#151515",
  fontWeight: 600,
  fontSize: 14,
  background: "rgba(255,255,255,0.75)",
  boxShadow: "0 8px 22px rgba(0,0,0,0.04)",
};